import * as React from 'react';
import RowSelectionCheckbox from './row-selection-checkbox';
import { ExpandIcon } from './expand';
import Cell from './cell';
import { isCustomExpand } from './common';

interface Filter {
  label: string | React.ReactElement;
  value: string | React.ReactElement;
  diabled?: boolean;
}

export interface TableColumn<T> {
  id?: string;
  cell?: (row: T, index: number) => string | React.ReactElement;
  accessorKey: keyof T;
  header: string | React.ReactElement | (() => React.ReactElement);
  columns?: TableColumn<T>[];
  // Whether to enable sorting, the default is false.
  // You can also customize the sort function
  // Every sorting function receives 2 rows and a column ID and are expected to compare the two rows using the column ID to return -1, 0, or 1 in ascending order. Here's a cheat sheet:
  // Return	Ascending Order
  // -1	a < b
  // 0	a === b
  // 1	a > b
  sorter?: ((rowA: T, rowB: T, index: number) => void) | boolean;
  // The filter menu item of the table header, if there is data, open the filter
  filters?: Filter[];
  filterMultiple?: boolean;
  onFilter?: (row: T, columnId: string, filterValue: string) => boolean;
  size?: number;
  style?: React.CSSProperties;
  enableHiding?: boolean;
  alignItems?: 'center' | 'flex-start' | 'flex-end';
}

export const formatColumns = (props) => {
  const { columns, expandable, rowSelection, dataSource, openExpand } = props;
  const haveData = Array.isArray(dataSource) && dataSource.length > 0;
  const selectionCheckbox = rowSelection &&
    haveData && {
      id: 'select',
      header: ({ table }) => {
        return (
          <RowSelectionCheckbox
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        );
      },
      cell: ({ row }) => (
        <RowSelectionCheckbox
          checked={row.getIsSelected()}
          indeterminate={row.getIsSomeSelected()}
          onChange={row.getToggleSelectedHandler()}
          disabled={!row.getCanSelect()}
        />
      ),
    };

  const expandColumn = {
    id: 'expand',
    header: () => null,
    cell: ({ row }) => <ExpandIcon row={row} />,
  };

  const formatColumns = generateColumns(columns, expandable);
  const newColumns = [selectionCheckbox, ...formatColumns];

  if (isCustomExpand(expandable) && !openExpand) {
    newColumns.push(expandColumn);
  }

  return newColumns.filter(Boolean);
};

const generateColumns = (customColumns, expandable) => {
  return customColumns.map((column, index) => {
    const {
      filters = [],
      filterMultiple = false,
      onFilter,
      sorter = false,
      cell,
      columns = [],
      enableHiding = false,
      ...others
    } = column;

    const newColumn = {
      ...others,
      enableHiding,
      cell: (cellProps) => <Cell customCell={cell} cellProps={cellProps} index={index} expandable={expandable} />,
      filters,
      filterMultiple,
      columns: generateColumns(columns, expandable),
      ...(typeof onFilter === 'function' && {
        filterFn: (row, columnId, filterValue) => onFilter(row.original, columnId, filterValue),
      }),
      ...(Array.isArray(filters) &&
        filters.length > 0 && {
          enableColumnFilter: true,
        }),
      ...(typeof sorter === 'boolean' && {
        enableSorting: sorter,
      }),
      ...(typeof sorter === 'function' && {
        enableSorting: true,
        sortingFn: (rowA, rowB, columnId) => sorter(rowA.original, rowB.original, columnId),
      }),
    };

    return newColumn;
  });
};
